<template>
  <el-dialog
    title="Seleccionar engranaje"
    width="80%"
    :model-value="visible"
    :before-close="handleCerrarDrawer"
    destroy-on-close
  >
    <el-row>
      <custom-data
        :label="'Medida al eje'"
        :spanColumn="4"
        :data="formatNumberLocale(displayMedidaEje, 4)"
      />
      <custom-data
        :label="'Medida al desarrollo'"
        :spanColumn="4"
        :data="formatNumberLocale(displayMedidaDesarrollo, 4)"
      />
      <custom-input-number
        v-model="toleranciaMin"
        :label="'Gap tolerancia min'"
        :spanColumn="4"
        :settings="{
          placeholder: 'Gap tolerancia min',
          precision: 0,
          step: 0.01,
          controls: false,
        }"
      />
      <custom-input-number
        v-model="toleranciaMax"
        :label="'Gap tolerancia máx'"
        :spanColumn="4"
        :settings="{
          placeholder: 'Gap tolerancia máx',
          min: 0,
          precision: 0,
          step: 0.01,
          controls: false,
        }"
      />
      <custom-data
        :label="'Unidad medida'"
        :spanColumn="4"
        :data="unidadMedida"
      />
      <custom-data
        :label="'Cantidad colores'"
        :spanColumn="4"
        :data="cantidadColores"
      />
    </el-row>
    <el-row v-if="isLoading"> Cargando datos, por favor espere... </el-row>
    <el-row v-else>
      <el-table
        :data="desarrollosFiltrados"
        :border="true"
        style="width: 100%"
      >
        <el-table-column
          prop="Repeticion"
          label="Repetición"
          width="100"
        />
        <el-table-column
          prop="Etiquetas"
          label="Etiq. des."
          width="100"
        />
        <el-table-column
          prop="GapDesarrollo"
          label="Gap Desarrollo"
          width="100"
        />
        <el-table-column
          prop="Engranaje"
          label="Engranaje"
        />
        <el-table-column
          prop="Desarrollo"
          label="Desarrollo"
        />
        <el-table-column
          prop="CantidadCilindros"
          label="Cant cilindros"
        />
        <el-table-column
          prop="CantidadMagneticos"
          label="Cant magnéticos"
        />
        <el-table-column
          prop="Recurso"
          label="Máquinas"
        />
        <el-table-column label="Acciones">
          <template #default="data">
            <el-button
              type="primary"
              @click="seleccionarEngranaje(data.row)"
              >Usar</el-button
            >
          </template>
        </el-table-column>
      </el-table>
    </el-row>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  onBeforeMount,
  computed,
} from 'vue';
import { useDesarrolloStore } from '@/stores/desarrollo/desarrollo_store.js';
import { useConfiguracionesPorDefectoStore } from '@/views/configuraciones_por_defecto/stores/configuraciones_por_defecto_store.js';
import { storeToRefs } from 'pinia';
import CustomData from '@/shared/components/custom_data.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import { formatNumberLocale } from '@/shared/utils/locale_number_utils.js';

const configuracionesPorDefectoStore = useConfiguracionesPorDefectoStore();
const desarrollosStore = useDesarrolloStore();
const { tolGapDesarrolloMin: gapToleranciaMinima, tolGapDesarrolloMax: gapToleranciaMaxima } =
  storeToRefs(configuracionesPorDefectoStore);
const { isLoading, desarrollos } = storeToRefs(desarrollosStore);
const { findAllHTTP: firstConfiguraciones } = configuracionesPorDefectoStore;

const toleranciaMin = ref(0);
const toleranciaMax = ref(0);
const FACTOR_CONVERSION = 25.4;

const emits = defineEmits(['cerrarBuscarEngranaje', 'seleccionarEngranaje']);

const props = defineProps({
  visible: { type: Boolean, default: false },
  medidaEje: { type: Number, required: true },
  medidaDesarrollo: { type: Number, required: true },
  unidadMedida: { type: String, required: true },
  cantidadColores: { type: Number, required: true },
});

const handleCerrarDrawer = () => {
  emits('cerrarBuscarEngranaje');
};

const displayMedidaEje = computed(() => {
  return props.unidadMedida === 'in' ? props.medidaEje / FACTOR_CONVERSION : props.medidaEje;
});
const displayMedidaDesarrollo = computed(() => {
  return props.unidadMedida === 'in'
    ? props.medidaDesarrollo / FACTOR_CONVERSION
    : props.medidaDesarrollo;
});

const seleccionarEngranaje = (engranaje) => {
  if (engranaje) {
    emits('seleccionarEngranaje', {
      MedidaEjeMm: props.medidaEje,
      MedidaEjeIn: props.medidaEje / FACTOR_CONVERSION,
      MedidaDesarrolloMm: props.medidaDesarrollo,
      MedidaDesarrolloIn: props.medidaDesarrollo / FACTOR_CONVERSION,
      Desarrollo: engranaje.Desarrollo,
      GapDesarrolloMm: engranaje.GapDesarrollo,
      GapDesarrolloIn: engranaje.GapDesarrollo / FACTOR_CONVERSION,
      EtiquetasDesarrollo: engranaje.Etiquetas,
      Engranaje: engranaje.Engranaje,
      CantidadCilindros: engranaje.CantidadCilindros,
      CantidadMagneticos: engranaje.CantidadMagneticos,
      Recurso: engranaje.Recurso,
    });
    handleCerrarDrawer();
  }
};

const desarrollosFiltrados = computed(() => {
  if (toleranciaMin.value !== null && toleranciaMax.value !== null) {
    const result = [];

    const medidaDesarrolloParaCalculo = props.medidaDesarrollo;
    if (!medidaDesarrolloParaCalculo || medidaDesarrolloParaCalculo <= 0) return [];

    desarrollos.value.forEach((i) => {
      const etiquetasPorDesarrollo = Math.round(i.Desarrollo / medidaDesarrolloParaCalculo);

      if (etiquetasPorDesarrollo <= 0) return;

      const repeticion = +(i.Desarrollo / etiquetasPorDesarrollo).toFixed(2);

      let gap = +(
        (i.Desarrollo - etiquetasPorDesarrollo * medidaDesarrolloParaCalculo) /
        etiquetasPorDesarrollo
      ).toFixed(3);
      if (Math.abs(gap) < 0.001) gap = 0.0;

      if (
        gap >= toleranciaMin.value &&
        repeticion <= medidaDesarrolloParaCalculo + toleranciaMax.value
      ) {
        i.CilindroTipoDesarrollo.forEach((c) => {
          result.push({
            Etiquetas: etiquetasPorDesarrollo,
            Repeticion: repeticion,
            GapDesarrollo: gap,
            Engranaje: i.Engranaje,
            Desarrollo: i.Desarrollo,
            CantidadCilindros: c.CantidadCilindros || 0,
            CantidadMagneticos: c.CantidadMagneticos || 0,
            Recurso: c.CilindroTipo?.Descripcion,
          });
        });
      }
    });
    return result;
  }
  return [];
});
onBeforeMount(async () => {
  await firstConfiguraciones();
  toleranciaMin.value = +(gapToleranciaMinima.value ?? 3);
  toleranciaMax.value = +(gapToleranciaMaxima.value ?? 5);
  await desarrollosStore.findAllDesarrollos({ page: 0 });
});
</script>

<style scoped></style>
